const dashboardDetailPath = (kind: string, id: string): string => {
  const plural =
    kind === "client"
      ? "clients"
      : kind === "supplier"
        ? "suppliers"
        : kind === "product"
          ? "products"
          : kind === "store"
            ? "stores"
            : kind === "load"
              ? "loads"
              : kind === "checker"
                ? "checkers"
                : null;
  if (!plural)
    throw new TypeError(`unsupported dashboard detail kind: ${kind}`);
  return `/dashboard/v2/details/${plural}/${encodeURIComponent(id)}`;
};

export const ENDPOINTS = {
  reports: {
    pedidos: "/reports/pedidos",
    pedidosExport: "/reports/pedidos/export",
    pedidoOrderPdf: (orderId: string) =>
      `/reports/pedidos/orders/${encodeURIComponent(orderId)}/pdf`,
  },
  auth: {
    login: "/login",
    me: "/me",
  },
  quotations: {
    list: "/quotations",
    availablePublications: "/quotations/available-publications",
    today: "/quotations/today",
    effective: "/quotations/effective",
    effectiveItems: "/quotations/effective-items",
    submissions: "/quotations/submissions",
    detail: (id: string) => `/quotations/${id}`,
    specials: (id: string) => `/quotations/${id}/specials`,
    targets: (id: string) => `/quotations/${id}/targets`,
    items: (id: string) => `/quotations/${id}/items`,
    item: (id: string, itemId: string) => `/quotations/${id}/items/${itemId}`,
    bootstrap: (id: string) => `/quotations/${id}/bootstrap`,
    copyInto: (sourceId: string, targetId: string) =>
      `/quotations/${sourceId}/copy-into/${targetId}`,
    copySpecialInto: (sourceId: string, specialId: string, targetId: string) =>
      `/quotations/${sourceId}/specials/${specialId}/copy-into/${targetId}`,
    audit: (id: string) => `/quotations/${id}/audit`,
    export: (id: string) => `/quotations/${id}/export`,
    exportData: (id: string) => `/quotations/${id}/export-data`,
    exportPdf: (id: string) => `/quotations/${id}/export.pdf`,
    assignments: (id: string) =>
      `/quotations/${encodeURIComponent(id)}/client-assignments`,
    assignmentIds: (id: string) =>
      `/quotations/${encodeURIComponent(id)}/client-assignments/client-ids`,
    activateAllAssignments: (id: string) =>
      `/quotations/${encodeURIComponent(id)}/client-assignments/activate-all`,
    deactivateAllAssignments: (id: string) =>
      `/quotations/${encodeURIComponent(id)}/client-assignments/deactivate-all`,
    deactivateAllPublications: "/quotations/client-assignments/deactivate-all",
  },
  products: {
    list: "/products",
    detail: (id: string) => `/products/${id}`,
  },
  supplierCatalog: {
    list: "/supplier/catalog/products",
    detail: (productId: string) => `/supplier/catalog/products/${productId}`,
  },
  supplierPedidos: {
    summary: "/supplier/pedidos",
  },
  supplierQuotations: {
    active: "/supplier/quotations/active",
    today: "/supplier/quotations/today",
    list: "/supplier/quotations",
    detail: (id: string) => `/supplier/quotations/${id}`,
    bootstrap: (id: string) => `/supplier/quotations/${id}/bootstrap`,
    submissions: "/supplier-quotations/submissions",
    items: (id: string) => `/supplier/quotations/${id}/items`,
    item: (id: string, itemId: string) =>
      `/supplier/quotations/${id}/items/${itemId}`,
  },
  productRequests: {
    supplierList: "/supplier/product-requests",
    pending: "/product-requests/pending",
    approveCreate: (id: string) => `/product-requests/${id}/approve-create`,
    approveMap: (id: string) => `/product-requests/${id}/approve-map`,
    reject: (id: string) => `/product-requests/${id}/reject`,
  },
  clients: {
    list: "/clients",
    detail: (id: string) => `/clients/${id}`,
  },
  dashboard: {
    operations: "/dashboard/v2/operations",
    supplierCurrentLoads: "/dashboard/v2/supplier/current-loads",
    analytics: "/dashboard/v2/analytics",
    detail: dashboardDetailPath,
    priceDrops: "/dashboard/v2/price-drops",
    ranking: (kind: string) => `/dashboard/v2/rankings/${kind}`,
    summary: "/dashboard/summary",
    rankings: "/dashboard/rankings",
    supplierReturns: "/dashboard/supplier-returns",
  },
  orders: {
    list: "/orders",
    enriched: "/orders/enriched",
    summary: "/orders/summary",
    overview: "/orders/overview",
    addItem: "/orders/add-item",
    detail: (id: string) => `/orders/${id}`,
    enrichedDetail: (id: string) => `/orders/${id}/enriched`,
    complete: (id: string) => `/orders/${id}/complete`,
    reopen: (id: string) => `/orders/${id}/reopen`,
    item: (id: string, itemId: string) => `/orders/${id}/items/${itemId}`,
    replaceItem: (id: string, itemId: string) =>
      `/orders/${id}/items/${itemId}/replace`,
    itemBonus: (id: string, itemId: string) =>
      `/orders/${id}/items/${itemId}/bonus`,
    legacyKgMigration: (id: string, itemId: string) =>
      `/orders/${id}/items/${itemId}/legacy-kg-migration`,
    changeRequest: (orderId: string) => `/orders/${orderId}/change-requests`,
    getChangeRequest: (crId: string) => `/orders/change-requests/${crId}`,
    changeRequestItems: (crId: string) =>
      `/orders/change-requests/${crId}/items`,
    updateChangeRequest: (crId: string) => `/orders/change-requests/${crId}`,
    deleteChangeRequest: (crId: string) => `/orders/change-requests/${crId}`,
    approveChangeRequest: (crId: string) =>
      `/orders/change-requests/${crId}/approve`,
    rejectChangeRequest: (crId: string) =>
      `/orders/change-requests/${crId}/reject`,
  },
  alerts: {
    list: "/alerts",
    tray: "/alerts/tray",
    count: "/alerts/count",
    create: "/alerts",
    updateStatus: (id: string) => `/alerts/${id}/status`,
    updateNote: (id: string) => `/alerts/${id}/note`,
    clear: "/alerts/seen",
  },
  suppliers: {
    list: "/suppliers",
    detail: (id: string) => `/suppliers/${id}`,
    boxTransactions: (id: string) => `/suppliers/${id}/box-transactions`,
    reportStatuses: "/suppliers/report-statuses",
    reportStatus: (id: string) => `/suppliers/${id}/reports/status`,
    reportMensal: (id: string) => `/suppliers/${id}/reports/mensal`,
    reportMensalData: (id: string) => `/suppliers/${id}/reports/mensal-data`,
    reportMensalGeralData: "/suppliers/reports/mensal-geral-data",
    reportCaixas: (id: string) => `/suppliers/${id}/reports/caixas`,
    reportCaixasData: (id: string) => `/suppliers/${id}/reports/caixas-data`,
    reportPedido: (id: string) => `/suppliers/${id}/reports/pedido`,
    reportPedidoData: (id: string) => `/suppliers/${id}/reports/pedido-data`,
    reportPedidoStores: (id: string) =>
      `/suppliers/${id}/reports/pedido-stores`,
  },
  supplierCaixas: {
    balance: "/supplier/caixas/balance",
    list: "/supplier/caixas",
  },
  caixas: {
    suppliers: "/caixas/suppliers",
    supplierTransactions: (id: string) =>
      `/caixas/suppliers/${id}/transactions`,
    undoTransaction: (id: string) => `/caixas/transactions/${id}/undo`,
  },
  checkins: {
    list: "/checkins",
    detail: (id: string) => `/checkins/${id}`,
    toggleStatus: (id: string) => `/checkins/${id}/status-toggle`,
    checkItem: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/check`,
    quickCheckItem: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/quick-check`,
    undoCheckItem: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/undo-check`,
    addReceipt: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/receipts`,
    closeShortfall: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/shortfall-close`,
    shortfallReopenItem: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/shortfall-reopen`,
    undoReceivingAction: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/undo-receiving-action`,
    undoReturnAction: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/undo-return-action`,
    requestReturn: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/return-request`,
    decideReturn: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/return-decision`,
    reopenReturnDecision: (checkinId: string, itemId: string) =>
      `/checkins/${checkinId}/items/${itemId}/return-decision-reopen`,
    suppliers: (id: string) => `/checkins/${id}/suppliers`,
    boxTransaction: (id: string) => `/checkins/${id}/box-transaction`,
    audit: (id: string) => `/checkins/${id}/audit`,
  },
  featureFlags: {
    list: "/feature-flags",
  },
  users: {
    list: "/users",
    detail: (id: string) => `/users/${id}`,
    storeAccess: (id: string) => `/users/${id}/store-access`,
    deactivate: (id: string) => `/users/${id}/deactivate`,
    reactivate: (id: string) => `/users/${id}/reactivate`,
    resetPassword: (id: string) => `/users/${id}/reset-password`,
  },
  me: {
    password: "/me/password",
    pin: "/me/pin",
    preferences: "/me/preferences",
  },
} as const;
